import { IGalleryItem } from '@galleries/models/galleriesStateModel';
//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { ShareStatusFilterValues } from '@share/constants/shareConstants';
import {
  BazaarvoiceNetworkModerationStatus,
  CampaignSyndicationStatsResponse,
  GetCampaignSyndicationStatsRequest,
  IBazaarvoiceDestinationClient,
  IShareCreatorClient,
  IShareMediaItem,
  IShareModerationStats,
  ISharePublisherClient,
  IShareRejectionReason,
  IShareTaggedProductsStats,
} from '@share/models/shareStateModel';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch, sessionFetch } from '@utils/sessionFetch';
import { ICuralateSession } from 'app/basic-session/ICuralateSession';
import { crl8 } from 'app/curalate.globals';

const SHARE_API_BASE_URL = '/api/v1/connect';

export interface IGetSharedMediaRequestParams {
  cuid?: string;
  offset?: number;
  limit?: number;
  searchText?: string;
  moderationStatus?: ShareStatusFilterValues;
}

export interface ISharedMediaResponse {
  items: IShareMediaItem[];
  totalItemCount: number;
}

export interface IGetBazaarvoiceNetworkSharedMediaRequestParams {
  cuid?: string;
  offset?: number;
  limit?: number;
  searchText?: string;
  moderationStatus?: BazaarvoiceNetworkModerationStatus;
}

export interface IGetBazaarvoiceNetworkSharedMediaResponse {
  items: IGalleryItem[];
  totalItemCount: number;
  isSyndicated?: boolean;
}

export interface IGetPublishersForCreatorResponse {
  publisherClients: ISharePublisherClient[];
}

export interface IGetCreatorsForPublisherResponse {
  creatorClients: IShareCreatorClient[];
}

export interface IGetClientRejectionReasons {
  reasons: IShareRejectionReason[];
}

export interface IModerationStatsResponse {
  moderationStats: IShareModerationStats;
}

function makeSyndicationEndpoint(
  session: ICuralateSession,
  endpoint: string,
): string {
  return `${session.baseUrls.syndication_gateway}${endpoint}`;
}

/**
 * Shares media for the cuids provided with the specified publisher via Connect.
 *
 * @param cuids The CUIDs to share
 * @param publisherClientId The Publisher to share CUIDs with. Not passed when sharing via BV syndication
 */
export async function shareMedia(
  cuids: string[],
  publisherClientId?: number,
  forceRequestReviewNow?: boolean,
): Promise<void> {
  let params;

  params = {
    publisherClientId,
    cuids,
  };

  if (forceRequestReviewNow !== undefined) {
    params.forceRequestReviewNow = forceRequestReviewNow;
  }

  const endpoint = makeSyndicationEndpoint(
    crl8.session,
    `${SHARE_API_BASE_URL}/media`,
  );
  await sessionFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * Removes media for the provided cuids which were previously added from Connect.
 *
 * @param cuids The CUIDs to remove from Connect.
 */
export async function removeSharedMedia(cuids: PartialCuid[]): Promise<void> {
  const params = {
    cuids,
  };

  const endpoint = makeSyndicationEndpoint(
    crl8.session,
    `${SHARE_API_BASE_URL}/remove-media`,
  );
  await sessionFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(params),
  });

  return;
}

/**
 * Gets the media shared by a ShareV2 creator to its publisher.
 * /api/v1/connect/media/:publisherClientId/:creatorClientId
 *
 * @param publisherClientId
 * @param creatorClientId
 * @param params - Pagination and filtering options
 *
 * @returns Promise<ISharedMediaResponse>
 */
export async function getSharedMedia(
  publisherClientId: number,
  creatorClientId: number,
  params: IGetSharedMediaRequestParams,
): Promise<ISharedMediaResponse> {
  const queryParams = $.param(params);
  const endpoint = makeSyndicationEndpoint(
    crl8.session,
    `${SHARE_API_BASE_URL}/media/${publisherClientId}/${creatorClientId}?${queryParams}`,
  );

  try {
    const res = await sessionFetch(endpoint);
    return (await res.json()) as ISharedMediaResponse;
  } catch {
    throw new Error(
      `Bad getSharedMedia response, publisherId: ${publisherClientId}, creatorId: ${creatorClientId}`,
    );
  }
}

/**
 * Gets the media shared across the BV network.
 * /api/v1/connect/bazaarvoice-network-media/:creatorClientId/
 *
 * @param creatorClientId
 * @param params - Pagination and filtering options
 *
 * @returns Promise<ISharedMediaResponse>
 */
export async function getBazaarvoiceNetworkSharedMedia(
  creatorClientId: number,
  params: IGetBazaarvoiceNetworkSharedMediaRequestParams,
): Promise<IGetBazaarvoiceNetworkSharedMediaResponse> {
  const queryParams = $.param(params);
  const endpoint = makeSyndicationEndpoint(
    crl8.session,
    `${SHARE_API_BASE_URL}/bazaarvoice-network-media/${creatorClientId}?${queryParams}`,
  );

  try {
    const res = await sessionFetch(endpoint);
    return (await res.json()) as IGetBazaarvoiceNetworkSharedMediaResponse;
  } catch {
    throw new Error(
      `Bad getBazaarvoiceNetworkSharedMedia response, creatorId: ${creatorClientId}`,
    );
  }
}

export async function getIsSyndicatedContentStatus(
  creatorClientId: string,
  cuid: string,
): Promise<IGetBazaarvoiceNetworkSharedMediaResponse> {
  const endpoint = makeSyndicationEndpoint(
    crl8.session,
    `${SHARE_API_BASE_URL}/bazaarvoice-network-media/${creatorClientId}/items/${cuid}/status`,
  );

  try {
    const res = await sessionFetch(endpoint);
    return (await res.json()) as IGetBazaarvoiceNetworkSharedMediaResponse;
  } catch {
    throw new Error(
      `Bad getBazaarvoiceNetworkSharedMedia response, creatorId: ${creatorClientId}`,
    );
  }
}

/**
 * Gets the publisher clients associated with the given ShareV2 creator
 * /api/v1/connect/creators/:creatorClientId/publishers
 *
 * @param creatorClientId
 *
 * @returns List of clients as a promise
 */
export async function getPublishersForCreator(
  creatorClientId: number,
): Promise<IGetPublishersForCreatorResponse> {
  const endpoint = makeSyndicationEndpoint(
    crl8.session,
    `${SHARE_API_BASE_URL}/creators/${creatorClientId}/publishers`,
  );
  try {
    const res = await sessionFetch(endpoint);
    return (await res.json()) as IGetPublishersForCreatorResponse;
  } catch {
    throw new Error(
      `Bad getPublishersForCreator response, creatorId: ${creatorClientId}`,
    );
  }
}

/**
 * Gets the creator clients associated with the given BV publisher via the Connect stream config
 * /api/v1/connect/creators/:creatorClientId/creators
 *
 * @param publisherClientId
 *
 * @returns List of clients as a promise
 */
export async function getCreatorsForConnectPublisherFromStream(
  publisherClientId: number,
): Promise<IGetCreatorsForPublisherResponse> {
  const endpoint = makeSyndicationEndpoint(
    crl8.session,
    `${SHARE_API_BASE_URL}/publisher/${publisherClientId}/creators/stream`,
  );

  try {
    const res = await sessionFetch(endpoint);
    return (await res.json()) as IGetCreatorsForPublisherResponse;
  } catch {
    throw new Error(
      `Bad getCreatorsForConnectPublisherFromStream response, publisherId: ${publisherClientId}`,
    );
  }
}

/**
 * Gets the rejection reasons associated with the given publisher client
 * /api/v1/connect/rejections/:publisherClientId/reasons
 *
 * @param publisherClientId
 *
 * @returns List of rejection reasons as a promise
 */
export async function getClientRejectionReasons(
  publisherClientId: ClientId,
): Promise<IGetClientRejectionReasons> {
  const endpoint = makeSyndicationEndpoint(
    crl8.session,
    `${SHARE_API_BASE_URL}/rejections/${publisherClientId}/reasons`,
  );
  try {
    const res = await sessionFetch(endpoint);
    return (await res.json()) as IGetClientRejectionReasons;
  } catch {
    throw new Error(
      `Bad getClientRejectionReasons response, publisherId: ${publisherClientId}`,
    );
  }
}

/**
 * Rejects a cuid for the provided rejection reason
 *
 * @param cuid The CUID to reject.
 */
export async function rejectSharedMedia(
  cuid: Cuid,
  rejectionCode: number,
): Promise<void> {
  const params = {
    cuid,
    rejectionCode,
  };

  const endpoint = makeSyndicationEndpoint(
    crl8.session,
    `${SHARE_API_BASE_URL}/reject`,
  );

  await sessionFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(params),
  });

  return;
}

/**
 * Get moderation stats for shared, approved, rejected, and published media
 * between a creator and publisher.
 * /api/v1/connect/moderation-stats/:publisherClientId/:creatorClientId
 *
 * @param publisherClientId
 * @param creatorClientId
 *
 * @returns Share moderation stats for shared, approved, rejected, and published media.
 */
export async function getModerationStats(
  publisherClientId: number,
  creatorClientId: number,
): Promise<IShareModerationStats> {
  const endpoint = makeSyndicationEndpoint(
    crl8.session,
    `${SHARE_API_BASE_URL}/moderation-stats/${publisherClientId}/${creatorClientId}`,
  );

  try {
    const res = await sessionFetch(endpoint);
    return (await res.json()) as IShareModerationStats;
  } catch {
    throw new Error(
      `Bad getModerationStats response, publisherId: ${publisherClientId}, creatorId: ${creatorClientId}`,
    );
  }
}

/**
 * Get tagged products stats for products tagged in shared media.
 * /api/v1/connect/tagged-products-stats/:publisherClientId/:creatorClientId
 *
 * @param publisherClientId
 * @param creatorClientId
 *
 * @returns List of tagged product stats objects.
 */
export async function getTaggedProductsStats(
  publisherClientId: number,
  creatorClientId: number,
): Promise<IShareTaggedProductsStats> {
  const endpoint = makeSyndicationEndpoint(
    crl8.session,
    `${SHARE_API_BASE_URL}/tagged-products-stats/${publisherClientId}/${creatorClientId}`,
  );

  try {
    const res = await sessionFetch(endpoint);
    return (await res.json()) as IShareTaggedProductsStats;
  } catch {
    throw new Error(
      `Bad getTaggedProductsStats response, publisherId: ${publisherClientId}, creatorId: ${creatorClientId}`,
    );
  }
}

export async function getBazaarvoiceNetworkDestinations(
  sourceClientId: ClientId,
): Promise<{ clients: IBazaarvoiceDestinationClient[] }> {
  const endpoint = `${SHARE_API_BASE_URL}/bazaarvoice-network-relationships/${sourceClientId}/destinations`;

  try {
    const res = await serializedGatewayFetch('syndication_gateway', endpoint);
    return res.json();
  } catch {
    throw new Error(
      `Bad getBazaarvoiceNetworkDestinations response, sourceClientId: ${sourceClientId}`,
    );
  }
}

/**
 * Gets all the data necessary to render VSN syndication stats
 *
 * GET /api/v1/connect/campaign-stats/:creatorClientId
 */

export const getCampaignSyndicationStats = async (
  req: GetCampaignSyndicationStatsRequest,
  init?: RequestInit,
): Promise<CampaignSyndicationStatsResponse> => {
  const params = makeParams(req);

  const res = await serializedGatewayFetch(
    'syndication_gateway',
    `${SHARE_API_BASE_URL}/campaign-stats/${req.creatorClientId}?${params}`,
    {
      ...init,
      method: 'GET',
    },
  );

  return await res.json<CampaignSyndicationStatsResponse>();
};
